import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Button, List,
    ListItemButton, ListItemIcon, ListItemText, Checkbox, Grid2, Paper, Typography,
    TextField, DialogActions, MenuItem
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Close, Save } from '@mui/icons-material';
import { myApi } from '../../../../config/axiosConfig';
import Swal from 'sweetalert2';

/*=============================================
FUNCIONES QUE SE UTILIZA PARA UN COMPONENTE DE LISTA
=============================================*/
function not(a, b) {
    return a.filter((value) => !b.includes(value));
}

function intersection(a, b) {
    return a.filter((value) => b.includes(value));
}

function ModalAsignarTema({ open, onClose, temas, refresh, isEditing = false, selectedFormulario = null, catTipoFormularios }) {

    /*=============================================
    USE STATE QUE SE UTILIZAN PARA EL ARRAY DE TEMAS
    =============================================*/
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);

    const [right, setRight] = useState();

    /*=============================================
    USE EFFECT QUE SE UTILIZA PARA ORGANIZAR LOS TEMAS SEGUN EL ARRAY JSON
    =============================================*/
    useEffect(() => {
        if (isEditing && selectedFormulario) {

            let temasJson;
            try {
                temasJson = JSON.parse(selectedFormulario.temas);
            } catch (error) {
                temasJson = [];
            }

            // Verificar si temasJson es un array
            if (!Array.isArray(temasJson)) {
                temasJson = [];
            }
            // Extraer solo los IDs de temasJson
            const temasJsonIds = temasJson.map(tema => tema.id_Tema);

            // Filtrar temas seleccionados y los restantes
            const TemasSeleccionados = temas.filter(tema => temasJsonIds.includes(tema.id_tema));
            const TemasRestantes = temas.filter(tema => !temasJsonIds.includes(tema.id_tema));

            setLeft(TemasSeleccionados);
            setRight(TemasRestantes);
        } else {
            setRight(temas);
            setLeft([]);
        }
    }, [isEditing, selectedFormulario, temas]);


    /*=============================================
    ASIGNACION DE LOS TEMAS A SU RESPECTIVO ARRAY
    =============================================*/
    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    /*=============================================
    FUNCION QUE CIERRA EL MODAL
    =============================================*/
    const handleOnClose = () => {
        onClose();
        setChecked([]);
        setLeft([]);
        setRight([]);
    };

    /*=============================================
    FUNCION QUE CAMBIA DE LUGAR LOS TEMAS
    =============================================*/
    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    /*=============================================
    FUNCION QUE AGREGA TEMAS A LA DERECHA
    =============================================*/
    const handleCheckedRight = (setFieldValue) => {
        const newRight = right.concat(leftChecked);
        const newLeft = not(left, leftChecked);
        setRight(newRight);
        setLeft(newLeft);
        setChecked(not(checked, leftChecked));

        // Solo enviar IDs a Formik
        setFieldValue("temas", newLeft.map(t => t.id_tema));
    };

    /*=============================================
    FUNCION QUE AGREGA TEMAS A LA IZQUIERDA
    =============================================*/
    const handleCheckedLeft = (setFieldValue) => {
        const newLeft = left.concat(rightChecked);
        const newRight = not(right, rightChecked);
        setLeft(newLeft);
        setRight(newRight);
        setChecked(not(checked, rightChecked));

        // Solo enviar IDs a Formik
        setFieldValue("temas", newLeft.map(t => t.id_tema));
    };

    /*=============================================
    FUNCION QUE AGREGA TODOS LOS TEMAS A LA DERECHA
    =============================================*/
    const handleAllRight = (setFieldValue) => {
        setRight(right.concat(left));
        setLeft([]);
        setChecked([]);

        // Solo enviar IDs a Formik
        setFieldValue("temas", []);
    };

    /*=============================================
    FUNCION QUE AGREGA TODOS LOS TEMAS A LA IZQUIERDA
    =============================================*/
    const handleAllLeft = (setFieldValue) => {
        setLeft(left.concat(right));
        setRight([]);
        setChecked([]);

        // Solo enviar IDs a Formik
        setFieldValue("temas", left.concat(right).map(t => t.id_tema));
    };

    /*=============================================
    COMPONENTE DE LA LISTA DE TEMAS
    =============================================*/
    const customList = (items) => (
        <Paper sx={{ width: 300, height: 330, overflow: 'auto', backgroundColor: '#DEE0E0' }}>
            <List dense component="div" role="list">
                {items.map((value) => {
                    const labelId = `transfer-list-item-${value.id_tema}-label`;

                    return (
                        <ListItemButton key={value.id_tema} role="listitem" onClick={handleToggle(value)}>
                            <ListItemIcon>
                                <Checkbox
                                    checked={checked.includes(value)}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={`Tema - ${value.nombre_tema}`} />
                        </ListItemButton>
                    );
                })}
            </List>
        </Paper>
    );

    /*=============================================
    DEFINICIÓN DE LA VALIDACIÓN DEL FORMULARIO DE FORMULARIOS
    =============================================*/
    const validationSchema = Yup.object().shape({
        nombreFormulario: Yup.string().required('Campo obligatorio'),
        descripcionFormulario: Yup.string().required('Campo obligatorio'),
        temas: Yup.array().min(1, 'Debe seleccionar al menos un tema'),
        tipo_formulario: Yup.string().required('Campo obligatorio').min(1, 'Debe seleccionar al menos una categoria'),
        formulario_aplica: Yup.string().required('Campo obligatorio'),
    });

    /*=============================================
    DEFINICIÓN DE VALORES INICIALES
    =============================================*/
    const initialValues = {
        nombreFormulario: selectedFormulario ? selectedFormulario.nombre_formulario : '',
        descripcionFormulario: selectedFormulario ? selectedFormulario.descripcion : '',
        temas: selectedFormulario ? JSON.parse(selectedFormulario.temas).map(tema => tema.id_tema) : [],
        idFormulario: selectedFormulario ? selectedFormulario.id_formulario : '',
        tipo_formulario: selectedFormulario ? selectedFormulario.id_tipoFormulario : '',
        formulario_aplica: selectedFormulario ? selectedFormulario.formulario_aplica : '',
    };

    /*=============================================
    FUNCION QUE ASIGNA LOS TEMAS A UN FORMULARIO
    =============================================*/
    const handleSubmit = async (values, isEditing) => {

        const ids = left.map(tema => tema.id_tema);

        if (isEditing) {
            const response = await myApi.put('/inspeccion/formularios/updateFormulario', values);
            if (response.data.success) {
                onClose()
                Swal.fire({
                    icon: 'success',
                    title: 'Formulario editado',
                });
                await refresh();
            }
        } else {
            try {
                const response = await myApi.post('/inspeccion/formularios/insertFormulario', values);
                if (response.data.success) {
                    onClose()
                    Swal.fire({
                        icon: 'success',
                        title: 'Formulario agregado',
                    });
                    await refresh();
                } else {
                    onClose()
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al agregar formulario',
                        customClass: {
                            container: 'swal2-container'
                        }
                    });
                }
            } catch (error) {
                console.error('Error submitting data:', error);
            }
        }
    };

    return (
        <Dialog open={open} onClose={handleOnClose} maxWidth="md" fullWidth>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleSubmit(values, isEditing);
                }}
            >
                {({ values, errors, touched, setFieldValue }) => (
                    <Form autoComplete='off'>
                        <DialogTitle>
                            {isEditing ? 'Editar Formulario' : 'Agregar Formulario'}
                            <IconButton
                                aria-label="close"
                                onClick={handleOnClose}
                                sx={{ position: 'absolute', right: 8, top: 8 }}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent dividers>
                            <Grid2 container direction="column" spacing={2} sx={{ alignItems: 'center' }}>
                                <Grid2 container spacing={2} sx={{ justifyContent: 'center', width: '100%' }}>
                                    <Grid2 size={{ xs: 4, md: 4 }}>
                                        <Field
                                            as={TextField}
                                            name="nombreFormulario"
                                            label="Nombre del formulario"
                                            fullWidth
                                            error={touched.nombreFormulario && Boolean(errors.nombreFormulario)}
                                            helperText={touched.nombreFormulario && errors.nombreFormulario}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 6, md: 4 }} >
                                        <Field
                                            as={TextField}
                                            name="descripcionFormulario"
                                            label="Descripción del formulario"
                                            fullWidth
                                            error={touched.descripcionFormulario && Boolean(errors.descripcionFormulario)}
                                            helperText={touched.descripcionFormulario && errors.descripcionFormulario}
                                        />
                                    </Grid2>

                                </Grid2>
                                <Grid2 container spacing={2} sx={{ justifyContent: 'center', width: '100%' }}>
                                    <Grid2 size={{ xs: 6, md: 4 }} >
                                        <Field
                                            as={TextField}
                                            select
                                            name="tipo_formulario"
                                            label="Categoria del formulario"
                                            fullWidth
                                            error={touched.tipo_formulario && Boolean(errors.tipo_formulario)}
                                            helperText={touched.tipo_formulario && errors.tipo_formulario}
                                        >
                                            {catTipoFormularios.map((tipoFormulario) => (
                                                <MenuItem key={tipoFormulario.id_tipoFormulario} value={tipoFormulario.id_tipoFormulario}>
                                                    {tipoFormulario.nombre_TipoFormulario}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid2>
                                    <Grid2 size={{ xs: 6, md: 4 }} >
                                        <Field
                                            as={TextField}
                                            select
                                            name="formulario_aplica"
                                            label="Formulario Aplicable para"
                                            fullWidth
                                            error={touched.formulario_aplica && Boolean(errors.formulario_aplica)}
                                            helperText={touched.formulario_aplica && errors.formulario_aplica}
                                        >
                                            <MenuItem value="tractor">Tractor</MenuItem>
                                            <MenuItem value="remolque">Remolque</MenuItem>
                                        </Field>
                                    </Grid2>
                                </Grid2>

                                <Grid2 container spacing={2} sx={{ justifyContent: 'center', mt: 2 }}>
                                    <Grid2 xs={6}>
                                        <Typography variant="h6">Temas Asignados</Typography>
                                    </Grid2>
                                    <Grid2 xs={6}>
                                        <Typography variant="h6">Temas Disponibles</Typography>
                                    </Grid2>
                                </Grid2>

                                <Grid2 container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                                    <Grid2>{customList(left)}</Grid2>
                                    <Grid2>
                                        <Grid2 container direction="column" sx={{ alignItems: 'center' }}>
                                            <Button onClick={() => handleAllRight(setFieldValue)} disabled={left.length === 0}>≫</Button>
                                            <Button onClick={() => handleCheckedRight(setFieldValue)} disabled={leftChecked.length === 0}>&gt;</Button>
                                            <Button onClick={() => handleCheckedLeft(setFieldValue)} disabled={rightChecked.length === 0}>&lt;</Button>
                                            <Button onClick={() => handleAllLeft(setFieldValue)} disabled={right.length === 0}>≪</Button>
                                        </Grid2>
                                    </Grid2>
                                    <Grid2>{customList(right)}</Grid2>
                                </Grid2>

                                {touched.temas && errors.temas && (
                                    <Typography color="error">{errors.temas}</Typography>
                                )}
                            </Grid2>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleOnClose} variant="contained" color="primary">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" color="primary" startIcon={<Save />}>
                                Guardar
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export default ModalAsignarTema;